import { Component, OnInit } from '@angular/core';
import { AlertService } from 'src/app/services/alert.service';
import { PosIntegrationSettingService } from 'src/app/services/pos-device.service';
import { PosIntegrationSettingRequest, PosIntegrationSettingView } from 'src/app/models/pos/pos-device';
import { PosDeviceProviderLabels, PosDeviceProviderType, PosLineMode, PosLineModeLabels } from 'src/app/models/enums/pos-enums';
import { extractErrorMessage } from '../../../accounting/shared/e-invoice.helpers';

/** Pavo'nun varsayılan KDV grubu eşlemesi (arka uç: PavoUniCloudConfig.DefaultTaxGroups). */
const PAVO_DEFAULT_TAX_GROUPS: { key: string, label: string, value: string }[] = [
  { key: '20', label: 'KDV %20', value: 'KDV20' },
  { key: '10', label: 'KDV %10', value: 'KDV10' },
  { key: '1', label: 'KDV %1', value: 'KDV1' },
  { key: '805', label: 'Altın özel matrah (805)', value: '805' },
  { key: '808', label: 'Gümüş özel matrah (808)', value: '808' },
  { key: '230', label: 'Külçe istisnası (230)', value: 'KDV0-230' },
];

/**
 * Tenant'ın POS sağlayıcı ayarı (pos-entegrasyonu.md K11, K16, K20).
 *
 * Entegratör kimliği (AppToken/ApiKey) uygulama ayarındadır; tenant yalnızca üye iş
 * yeri kimliğini (MerchantUid) girer. Kendi entegratör hesabıyla gelen müşteri için
 * anahtar alanları açıktır; kayıtlı anahtar ekrana hiç dönmez, boş bırakılan alan
 * kayıtlıyı korur.
 *
 * Sağlayıcıya özgü ayar (Pavo: KDV grubu eşlemesi, ödeme aracı…) JSON olarak saklanır
 * ve yalnızca varsayılandan farklı olanı taşır — sunucu varsayılanların üstüne yazar.
 */
@Component({
  selector: 'app-pos-integration-settings',
  templateUrl: './pos-integration-settings.component.html',
})
export class PosIntegrationSettingsComponent implements OnInit {
  readonly providers = [PosDeviceProviderType.PavoUniCloud, PosDeviceProviderType.Mock];
  readonly providerLabels = PosDeviceProviderLabels;
  readonly lineModes = [PosLineMode.RealProducts, PosLineMode.CommodityRules];
  readonly lineModeLabels = PosLineModeLabels;
  readonly ProviderType = PosDeviceProviderType;
  readonly taxGroupDefaults = PAVO_DEFAULT_TAX_GROUPS;

  providerType = PosDeviceProviderType.PavoUniCloud;
  view: PosIntegrationSettingView | null = null;
  form: PosIntegrationSettingRequest = this.emptyForm(PosDeviceProviderType.PavoUniCloud);

  /** Sağlayıcı ayarının ekrandaki hali; kaydederken JSON'a döner. */
  config: any = {};
  taxGroups: Record<string, string> = {};
  configJson = '';
  configJsonError: string | null = null;
  showAdvanced = false;

  loading = false;
  saving = false;
  testing = false;
  testResult: { isSuccess: boolean, message: string } | null = null;

  constructor(
    private service: PosIntegrationSettingService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.testResult = null;
    this.service.get(this.providerType).subscribe({
      next: res => {
        this.apply(res.data ?? null);
        this.loading = false;
      },
      error: error => {
        this.alertService.showErrorsToast([extractErrorMessage(error, 'POS ayarı alınamadı.')]);
        this.loading = false;
      },
    });
  }

  onProviderChanged(): void {
    this.load();
  }

  private apply(view: PosIntegrationSettingView | null): void {
    this.view = view;
    this.form = view
      ? {
          providerType: view.providerType,
          isActive: view.isActive,
          isTestMode: view.isTestMode,
          merchantUid: view.merchantUid,
          apiKey: null,
          appToken: null,
          clearOwnCredentials: false,
          providerConfigJson: view.providerConfigJson,
          defaultLineMode: view.defaultLineMode || PosLineMode.RealProducts,
          anonymousSaleLimit: view.anonymousSaleLimit,
          sendCashToDevice: !!view.sendCashToDevice,
        }
      : this.emptyForm(this.providerType);
    this.loadConfig(this.form.providerConfigJson);
  }

  get hasOwnCredentials(): boolean {
    return !!(this.view?.apiKeyMasked || this.view?.appTokenMasked);
  }

  // ── Sağlayıcı ayarı (JSON) ─────────────────────────────────────────────

  private loadConfig(json: string | null | undefined): void {
    try {
      this.config = json ? JSON.parse(json) : {};
    } catch {
      this.config = {};
    }
    if (!this.config || typeof this.config !== 'object') this.config = {};
    this.taxGroups = { ...(this.config.taxGroups || this.config.TaxGroups || {}) };
    delete this.config.TaxGroups;
    this.configJsonError = null;
    this.syncConfigJson();
  }

  /** Alanlardan JSON'u yeniden yazar — yalnızca varsayılandan farklı olanlar. */
  syncConfigJson(): void {
    const taxGroups: Record<string, string> = {};
    for (const group of PAVO_DEFAULT_TAX_GROUPS) {
      const value = (this.taxGroups[group.key] || '').trim();
      if (value && value !== group.value) taxGroups[group.key] = value;
    }
    // Ekranda olmayan anahtarlar (elle JSON'a yazılmış) korunur.
    Object.keys(this.taxGroups)
      .filter(key => !PAVO_DEFAULT_TAX_GROUPS.some(group => group.key === key) && this.taxGroups[key])
      .forEach(key => taxGroups[key] = this.taxGroups[key]);

    const config = { ...this.config };
    if (Object.keys(taxGroups).length) config.taxGroups = taxGroups; else delete config.taxGroups;
    if (config.cardMediator === null || config.cardMediator === '' || config.cardMediator === undefined) delete config.cardMediator;
    if (config.useJewellerySale !== false) delete config.useJewellerySale;

    this.config = config;
    this.configJson = Object.keys(config).length ? JSON.stringify(config, null, 2) : '';
  }

  setUseJewellerySale(value: boolean): void {
    this.config.useJewellerySale = value;
    this.syncConfigJson();
  }

  /** Gelişmiş: JSON elle düzenlendi — alanlara geri okunur. */
  onConfigJsonEdited(): void {
    if (!this.configJson.trim()) {
      this.configJsonError = null;
      this.loadConfig(null);
      return;
    }
    try {
      JSON.parse(this.configJson);
      const edited = this.configJson;
      this.loadConfig(edited);
    } catch {
      this.configJsonError = 'JSON geçersiz.';
    }
  }

  // ── Kaydet / dene ─────────────────────────────────────────────────────

  save(): void {
    const messages: string[] = [];
    if (this.form.isActive && this.providerType === PosDeviceProviderType.PavoUniCloud && !this.form.merchantUid?.trim())
      messages.push('Pavo için üye iş yeri kimliği (MerchantUid) girilmelidir.');
    if (!(this.form.anonymousSaleLimit > 0))
      messages.push('Müşterisiz satış limiti sıfırdan büyük olmalıdır.');
    if (this.configJsonError)
      messages.push('Sağlayıcı ayarı (JSON) geçersiz.');
    if (messages.length) {
      this.alertService.showErrorsToast(messages);
      return;
    }

    const request: PosIntegrationSettingRequest = {
      ...this.form,
      providerType: this.providerType,
      merchantUid: this.form.merchantUid?.trim() || null,
      apiKey: this.form.apiKey?.trim() || null,
      appToken: this.form.appToken?.trim() || null,
      providerConfigJson: this.configJson.trim() || null,
      anonymousSaleLimit: Number(this.form.anonymousSaleLimit),
      defaultLineMode: Number(this.form.defaultLineMode),
    };

    this.saving = true;
    this.service.save(request).subscribe({
      next: res => {
        this.saving = false;
        this.apply(res.data ?? null);
        this.alertService.showSuccess('POS ayarı kaydedildi.');
      },
      error: error => {
        this.saving = false;
        this.alertService.showErrorsToast([extractErrorMessage(error, 'POS ayarı kaydedilemedi.')]);
      },
    });
  }

  clearOwnCredentials(): void {
    this.alertService
      .showConfirmAlert('Firmanın kendi entegratör anahtarları silinecek; uygulamanın entegratör kimliği kullanılacak.', 'Sil', 'Vazgeç')
      .then(result => {
        if (!result.isConfirmed) return;
        this.form.clearOwnCredentials = true;
        this.form.apiKey = null;
        this.form.appToken = null;
        this.save();
      });
  }

  /** Kayıtlı ayarla bağlantıyı dener — kaydedilmemiş değişiklik denenmez. */
  test(): void {
    this.testing = true;
    this.testResult = null;
    this.service.test(this.providerType).subscribe({
      next: res => {
        this.testing = false;
        const ok = !!res.data?.isSuccess;
        this.testResult = { isSuccess: ok, message: res.data?.message || (ok ? 'Bağlantı başarılı.' : 'Bağlantı kurulamadı.') };
      },
      error: error => {
        this.testing = false;
        this.testResult = { isSuccess: false, message: extractErrorMessage(error, 'Bağlantı kurulamadı.') };
      },
    });
  }

  private emptyForm(providerType: PosDeviceProviderType): PosIntegrationSettingRequest {
    return {
      providerType,
      isActive: false,
      isTestMode: true,
      merchantUid: null,
      apiKey: null,
      appToken: null,
      clearOwnCredentials: false,
      providerConfigJson: null,
      defaultLineMode: PosLineMode.RealProducts,
      anonymousSaleLimit: 50000,
      sendCashToDevice: false,
    };
  }
}
